/**
 * Granularity for {@link timeDurationToTimeUnits}: whether to stop at whole minutes
 * or include remaining whole seconds.
 */
export const TimeDurationToTimeUnitsGranularity = {
  MINUTES: "minutes",
  SECONDS: "seconds",
} as const;

export type TimeDurationToTimeUnitsGranularityType =
  (typeof TimeDurationToTimeUnitsGranularity)[keyof typeof TimeDurationToTimeUnitsGranularity];

/**
 * Converts a time duration in milliseconds to human-readable chunks (days, hours,
 * minutes, and optionally seconds).
 *
 * @param time - Duration in milliseconds.
 * @param granularity - When `"minutes"` (default), sub-minute remainder is dropped. When `"seconds"`, an extra seconds segment is appended after minutes.
 * @returns An array of `{ value, unit }`, e.g. `[{ value: "1", unit: "Day" }, { value: "12", unit: "Hours" }, { value: "3", unit: "Minutes" }]`.
 */
export const timeDurationToTimeUnits = (
  time: number,
  granularity: TimeDurationToTimeUnitsGranularityType = TimeDurationToTimeUnitsGranularity.MINUTES
): Array<{ value: string; unit: string }> => {
  const oneSecondLength = 1000;
  const oneMinuteLength = 60 * oneSecondLength;
  const oneHourLength = 60 * oneMinuteLength;
  const oneDayLength = 24 * oneHourLength;

  let remainder = time;

  const days = Math.floor(remainder / oneDayLength);
  remainder -= days * oneDayLength;

  const hours = Math.floor(remainder / oneHourLength);
  remainder -= hours * oneHourLength;

  const minutes = Math.floor(remainder / oneMinuteLength);
  remainder -= minutes * oneMinuteLength;

  const seconds =
    granularity === TimeDurationToTimeUnitsGranularity.SECONDS
      ? Math.floor(remainder / oneSecondLength)
      : 0;

  const durations: Array<{ value: string; unit: string }> = [];

  if (days > 0) {
    durations.push({
      value: `${days}`,
      unit: days === 1 ? "Day" : "Days",
    });
  }

  if (hours > 0) {
    durations.push({
      value: `${hours}`,
      unit: hours === 1 ? "Hour" : "Hours",
    });
  }

  if (minutes > 0) {
    durations.push({
      value: `${minutes}`,
      unit: minutes === 1 ? "Minute" : "Minutes",
    });
  }

  if (granularity === TimeDurationToTimeUnitsGranularity.SECONDS) {
    durations.push({
      value: `${seconds}`,
      unit: seconds === 1 ? "Second" : "Seconds",
    });
  }

  return durations;
};

/**
 * Formats a date as local calendar date text (`YYYY-MM-DD`).
 *
 * @param date - Date value to format.
 * @returns Local date string with zero-padded month/day.
 */
export const getLocalIsoDateString = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * Formats a date as local ISO-8601 date-time text with timezone offset.
 *
 * Example: `2026-05-25T12:34:56-04:00`.
 *
 * @param date - Date value to format.
 * @returns Local date-time string with timezone offset.
 */
export const getLocalIsoDateTimeString = (date: Date = new Date()): string => {
  const pad = (value: number) => String(value).padStart(2, "0");
  const offsetMinutes = -date.getTimezoneOffset();
  const offsetSign = offsetMinutes >= 0 ? "+" : "-";
  const absoluteOffsetMinutes = Math.abs(offsetMinutes);

  return `${getLocalIsoDateString(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}${offsetSign}${pad(Math.floor(absoluteOffsetMinutes / 60))}:${pad(absoluteOffsetMinutes % 60)}`;
};

/**
 * Reads the day-of-month from an ISO date string (`YYYY-MM-DD`) or date-time
 * string that starts with the same format.
 *
 * @param localIsoDateString - Date/date-time string in ISO local format.
 * @returns Day-of-month value in range 1..31.
 * @throws Error when the input does not contain a valid day value.
 */
export const getDayOfMonthFromLocalIsoDateString = (
  localIsoDateString: string
): number => {
  const dayOfMonth = Number(localIsoDateString.slice(8, 10));

  if (!Number.isFinite(dayOfMonth) || dayOfMonth < 1 || dayOfMonth > 31) {
    throw new Error(
      `Could not parse day of month from local ISO date string: ${localIsoDateString}`
    );
  }

  return dayOfMonth;
};
